/**
 * `scope` — what the widget diagnoses (trades), where (service area), how
 * (photo / sound / spec 013's live conversation) and the extra questions.
 * Split into sub-parts so onboarding screen 2 can show only trades + area.
 */
import { useState, type ReactNode } from 'react';
import { useSession } from '../../auth/session';
import { Link } from '../../router';
import { Field, Switch } from '../../ui/kit';
import { useDraft } from '../draft';
import { TRADES, type CustomQuestion } from '../model';
import { issuesFor } from '../validate';
import { AppHandoffStatus, PathSelect, PathSwitch, SectionCard, useAppHandoff, usePath } from './common';

export function TradesPicker() {
  const trades = usePath<string[]>('scope.trades');
  const oos = usePath<string>('scope.out_of_scope_behavior');
  const app = useAppHandoff();
  const selected = new Set(trades.value);
  const toggle = (slug: string) => {
    const next = new Set(selected);
    if (next.has(slug)) next.delete(slug);
    else next.add(slug);
    trades.set(TRADES.map((t) => t.slug).filter((s) => next.has(s)));
  };
  return (
    <fieldset className="stack-sm">
      <legend className="label" style={{ marginBottom: 8 }}>
        Trades {selected.size === 0 ? <span className="faint">· none selected means every trade</span> : <span className="faint">· {selected.size} of {TRADES.length}</span>}
      </legend>
      <div className="chips">
        {TRADES.map((t) => (
          <button key={t.slug} type="button" className="chip" aria-pressed={selected.has(t.slug)} onClick={() => toggle(t.slug)}>
            {t.label}
          </button>
        ))}
      </div>
      {trades.issues.length ? <p className="err small" role="alert">{trades.issues[0]?.message}</p> : null}
      <PathSelect
        path="scope.out_of_scope_behavior"
        label="When a problem is outside these trades"
        options={[
          { value: 'diagnose_then_message', label: 'Diagnose, then show my out-of-scope message' },
          { value: 'diagnose_then_finderly', label: 'Diagnose, then offer the FinderlyFix app' },
          { value: 'refuse', label: 'Do not diagnose it' },
        ]}
      />
      {oos.value === 'diagnose_then_finderly' ? (
        <AppHandoffStatus context={app.enabled ? undefined : 'Visitors see your out-of-scope message and no button.'} />
      ) : null}
    </fieldset>
  );
}

export function ServiceAreaEditor() {
  const mode = usePath<'none' | 'postal_codes'>('scope.service_area.mode');
  const codes = usePath<string[]>('scope.service_area.postal_codes');
  const [text, setText] = useState(codes.value.join(', '));
  return (
    <div className="stack">
      <PathSelect
        path="scope.service_area.mode"
        label="Service area"
        options={[
          { value: 'none', label: 'Anywhere in the US' },
          { value: 'postal_codes', label: 'Only these ZIP codes or prefixes' },
        ]}
      />
      {mode.value === 'postal_codes' ? (
        <Field
          label="ZIP codes and prefixes"
          hint="Comma or space separated. A prefix like 981 covers 98101–98199. Up to 500."
          issues={codes.issues}
        >
          {(p) => (
            <textarea
              {...p}
              className="input input-mono"
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                codes.set(e.target.value.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean));
              }}
            />
          )}
        </Field>
      ) : null}
      {mode.value === 'postal_codes' ? (
        <PathSelect
          path="scope.service_area.out_of_area_behavior"
          label="When a visitor is outside the area"
          options={[
            { value: 'capture_anyway', label: 'Take the request anyway' },
            { value: 'finderly_network', label: 'Offer the FinderlyFix network' },
            { value: 'refuse', label: 'Do not take the request' },
          ]}
        />
      ) : null}
    </div>
  );
}

const MODE_LABELS = { photo: 'Photo', audio: 'Sound', live: 'Talk live', chat: 'Chat' } as const;
type CaptureMode = keyof typeof MODE_LABELS;

/** `scope.default_mode`: the tab a visitor lands on, from the modes that are on. */
export function DefaultModeSelect() {
  const draft = useDraft();
  const modes = draft.edited.scope.modes;
  const value = draft.edited.scope.default_mode;
  const on = (Object.keys(MODE_LABELS) as CaptureMode[]).filter((m) => modes[m]);
  return (
    <Field
      label="Tab visitors land on"
      hint="Only modes that are on are listed. Talk live and Chat share one tab; picking Chat makes it the main button there. “Widget’s choice” opens that tab where either is on, otherwise Photo."
      issues={issuesFor(draft.issues, 'scope.default_mode')}
    >
      {(p) => (
        <select
          {...p}
          className="input"
          value={value ?? ''}
          onChange={(e) => draft.update('scope.default_mode', e.target.value === '' ? null : e.target.value)}
        >
          <option value="">Widget’s choice</option>
          {on.map((m) => (
            <option key={m} value={m}>
              {MODE_LABELS[m]}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}

/** A mode switch that also clears the landing tab when it switches that tab's mode off. */
function ModeSwitch({ mode, label, description }: { mode: CaptureMode; label: ReactNode; description: ReactNode }) {
  const draft = useDraft();
  return (
    <Switch
      label={label}
      description={description}
      checked={Boolean(draft.edited.scope.modes[mode])}
      onChange={(checked) => {
        draft.update(`scope.modes.${mode}`, checked);
        if (!checked && draft.edited.scope.default_mode === mode) draft.update('scope.default_mode', null);
      }}
    />
  );
}

export function CaptureModes() {
  const live = usePath<boolean>('scope.modes.live');
  // Whether the server's live switch is on (whoami). Never hides the setting:
  // it only says whether turning it on takes effect now.
  const available = useSession().whoami?.widget_live_available;
  const chatAvailable = useSession().whoami?.widget_chat_available;
  return (
    <div>
      <div className="label" style={{ marginBottom: 4 }}>
        How visitors describe the problem
      </div>
      <ModeSwitch mode="photo" label="Photo" description="Snap or upload a picture." />
      <ModeSwitch mode="audio" label="Sound" description="Record the noise it makes." />
      <ModeSwitch
        mode="live"
        label="Live conversation"
        description={
          <>
            Talk it through with the FinderlyFix assistant, by voice.
            {available === false ? ' Not available yet: your choice is saved and takes effect once FinderlyFix turns live conversations on.' : ''} If
            your site has a Content-Security-Policy, the <Link to="/setup/install">Install page</Link> lists what to add.
          </>
        }
      />
      <ModeSwitch
        mode="chat"
        label="Chat"
        description={
          <>
            Type to the same assistant instead of talking. No microphone needed.
            {chatAvailable === false ? ' Not available yet: your choice is saved and takes effect once FinderlyFix turns chat on.' : ''}
          </>
        }
      />
      {live.value ? (
        <PathSwitch path="scope.live.camera" label="Let the live conversation use the camera" description="The visitor can show the problem as well as describe it." />
      ) : null}
      <DefaultModeSelect />
    </div>
  );
}

function newQuestion(index: number): CustomQuestion {
  return { id: `q_${index + 1}`, trade: null, label: '', type: 'text', options: [], required: false };
}

export function CustomQuestions() {
  const qs = usePath<CustomQuestion[]>('scope.custom_questions');
  const list = qs.value;
  const patch = (i: number, change: Partial<CustomQuestion>) => qs.set(list.map((q, j) => (j === i ? { ...q, ...change } : q)));
  const issuesAt = (i: number, key: string) => qs.issues.filter((x) => x.path === `scope.custom_questions.${i}.${key}`);
  return (
    <div className="stack">
      <div className="row-between">
        <div>
          <div className="label">Extra questions</div>
          <p className="small muted">Asked after the diagnosis, per trade. Up to 20.</p>
        </div>
        <button type="button" className="btn btn-sm" disabled={list.length >= 20} onClick={() => qs.set([...list, newQuestion(list.length)])}>
          Add question
        </button>
      </div>
      {list.map((q, i) => (
        <div className="qcard" key={i}>
          <div className="grid-2">
            <Field label="Question" issues={issuesAt(i, 'label')}>
              {(p) => <input {...p} className="input" value={q.label} onChange={(e) => patch(i, { label: e.target.value })} />}
            </Field>
            <Field label="Reference id" hint="Sent with the answer on each lead, so your systems can tell questions apart." issues={issuesAt(i, 'id')}>
              {(p) => <input {...p} className="input input-mono" value={q.id} onChange={(e) => patch(i, { id: e.target.value.trim() })} />}
            </Field>
          </div>
          <div className="grid-3">
            <Field label="Answer type">
              {(p) => (
                <select {...p} className="input" value={q.type} onChange={(e) => patch(i, { type: e.target.value as CustomQuestion['type'] })}>
                  <option value="text">Free text</option>
                  <option value="select">Choice</option>
                  <option value="boolean">Yes / no</option>
                </select>
              )}
            </Field>
            <Field label="Only for trade">
              {(p) => (
                <select {...p} className="input" value={q.trade ?? ''} onChange={(e) => patch(i, { trade: e.target.value || null })}>
                  <option value="">Every trade</option>
                  {TRADES.map((t) => (
                    <option key={t.slug} value={t.slug}>
                      {t.label}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <label className="check" style={{ alignSelf: 'end', paddingBottom: 8 }} htmlFor={`custom-q-${i}-required`}>
              <input id={`custom-q-${i}-required`} type="checkbox" checked={q.required} onChange={(e) => patch(i, { required: e.target.checked })} />
              <span className="t">Required</span>
            </label>
          </div>
          {q.type === 'select' ? (
            <Field label="Options" hint="One per line." issues={issuesAt(i, 'options')}>
              {(p) => (
                <textarea
                  {...p}
                  className="input"
                  value={q.options.join('\n')}
                  onChange={(e) => patch(i, { options: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) })}
                />
              )}
            </Field>
          ) : null}
          <div>
            <button type="button" className="btn btn-sm btn-danger" onClick={() => qs.set(list.filter((_, j) => j !== i))}>
              Remove question
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function ScopeSection() {
  return (
    <SectionCard id="sec-scope" title="Scope" description="What the widget diagnoses, where, and how visitors describe it.">
      <TradesPicker />
      <hr className="divider" />
      <ServiceAreaEditor />
      <hr className="divider" />
      <CaptureModes />
      <hr className="divider" />
      <CustomQuestions />
    </SectionCard>
  );
}
